import { useMemo } from 'react';
import { useSplunkTheme } from '@splunk/themes';
import { toPx } from '@splunk/dashboard-utils';

type TextWidth = `${number}px`;

const CachedTextWidth = new Map<string, TextWidth>();

export const useMeasuredTextWidth = (
    message: string | undefined,
    defaultWidth: TextWidth = '0px'
) => {
    const { fontFamily, fontSize } = useSplunkTheme();
    return useMemo(() => {
        if (!message) {
            // no message = no calculation to perform
            return defaultWidth;
        }

        const font = `${fontSize} ${fontFamily}`;
        const cachedWidth = CachedTextWidth.get(`[${font}] ${message}`);
        if (cachedWidth) {
            // message already cached in the current font, return measured width
            return cachedWidth;
        }

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (!context) {
            // no context = no calculation
            return defaultWidth;
        }

        context.font = font;

        const textWidth = Math.ceil(context.measureText(message).width);
        if (textWidth) {
            const textWidthPx = toPx(textWidth) as TextWidth;

            CachedTextWidth.set(`[${font}] ${message}`, textWidthPx);
            return textWidthPx;
        }

        return defaultWidth;
    }, [defaultWidth, fontFamily, fontSize, message]);
};
